import { useEffect, useState } from 'react'

import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import { Select } from 'src/components/Select/Select'
import { ModalCancelButton } from 'src/lib/chakra-theme/components'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'
import {
  ResendReservationEmailDocument,
  ResendReservationEmailModal_TrialPatientFragment,
} from 'src/lib/gql-client'
import { useGqlMutation } from 'src/lib/gql-client/request'
import { formatDate } from 'src/utils/formatDate'

type Props = {
  isOpen: boolean
  trialPatient: ResendReservationEmailModal_TrialPatientFragment
  partnerHospitalName: string | undefined
  onClose: () => void
}

export const ResendReservationEmailModal: React.FC<Props> = ({
  isOpen,
  trialPatient,
  partnerHospitalName,
  onClose,
}) => {
  const { reservations } = trialPatient

  const expReservations =
    reservations.filter(r => r.timeStatus === 'Upcoming') ?? []

  const now = dayjs()
  const defaultExpReservation =
    expReservations.find(r => dayjs(r.reservedAt).isAfter(now))
      ?.explanationReservation ||
    expReservations[expReservations.length - 1].explanationReservation

  if (expReservations.length === 0 || !defaultExpReservation) {
    throw new Error('面談予約が存在しません')
  }

  const [explanationReservationUid, setExplanationReservationUid] = useState(
    defaultExpReservation.explanationReservationUid,
  )

  // モーダルが閉じられたときに初期値に戻す
  useEffect(() => {
    if (!isOpen) {
      setExplanationReservationUid(
        defaultExpReservation.explanationReservationUid,
      )
    }
  }, [isOpen, defaultExpReservation.explanationReservationUid])

  useEffect(() => {
    if (defaultExpReservation) {
      setExplanationReservationUid(
        defaultExpReservation.explanationReservationUid,
      )
    }
  }, [defaultExpReservation])

  const toast = useMirohaToast()

  const { request } = useGqlMutation(ResendReservationEmailDocument, {
    onSuccess: () => {
      onClose()
      toast({
        status: 'success',
        title: '面談案内メールを送信しました。',
      })
    },
  })

  // explanationReservationがnullの場合は表示しない
  const selectItems = expReservations.flatMap(r => {
    if (!r.explanationReservation) return []
    return {
      value: r.explanationReservation.explanationReservationUid,
      label: (
        <HStack>
          <Text>{formatDate(r.reservedAt, 'YYYY/MM/DD (ddd) HH:mm')}</Text>
          <Text>
            案内先：
            {r.explanationReservation.reservationInviteeType === 'Patient'
              ? '患者'
              : partnerHospitalName}
          </Text>
        </HStack>
      ),
    }
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>面談案内メールを再送</ModalHeader>
        <ModalBody>
          <Stack spacing="4">
            <Text>以下の面談URLを再送します。</Text>
            <Select
              value={explanationReservationUid}
              items={selectItems}
              isDisabled={selectItems.length === 1}
              onChange={setExplanationReservationUid}
            />
          </Stack>
        </ModalBody>
        <ModalFooter>
          <ModalCancelButton />
          <Button
            onClick={async () => {
              if (!explanationReservationUid) return
              await request({
                input: {
                  explanationReservationUid,
                },
              })
            }}
          >
            送信する
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
